import { defineComponent, h } from 'vue';
import {
    QBtn, QBanner, QSpinner, QDialog, QCard, QCardSection, QCardActions,
    QInput, QOptionGroup, QIcon, QTooltip, QSelect,
} from 'quasar';
import { AgGridVue } from 'ag-grid-vue3';
import utils from '../../../../../Shared/utils.jsx';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import '../../../css/BasicButton.css';
import formatApiError from './formatApiError.js';
import { filterReleasePinInput, isReservedReleasePin } from '../debugger/flowV2WebUrl.js';
import './flowv2GridActions.css';

const BTN = `${SENTENCE_CASE_BUTTON_CLASS} q-my-xs`;

function formatReleaseFailure(res, fallback) {
    if (!res)
        return fallback;
    const detail = formatApiError(res);
    if (detail && detail !== 'Unknown error')
        return detail;
    return res.Reason || fallback;
}

function defaultShelvesetName(label) {
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
    const safe = (label || 'release').replace(/[^\w.-]+/g, '_');
    return `pre-pull-${safe}-${stamp}`;
}

function releaseIdOf(row) {
    return row?.ReleaseID || row?.ID || null;
}

function formatDepsCount(row) {
    const ids = row?.BundledProjectIDs;
    if (!Array.isArray(ids))
        return '—';
    return String(Math.max(0, ids.length - 1));
}

function formatCreatedAt(value) {
    if (!value)
        return '—';
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime()))
            return String(value);
        return d.toLocaleString();
    }
    catch {
        return String(value);
    }
}

function iconActionBtn({ icon, tooltip, color, disable, onClick }) {
    return h(QBtn, {
        flat: true,
        round: true,
        dense: true,
        size: 'sm',
        color: color || 'primary',
        icon,
        disable: !!disable,
        onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!disable)
                onClick?.(e);
        },
        onMousedown: (e) => {
            e.stopPropagation();
        },
    }, {
        default: () => h(QTooltip, {
            'transition-show': 'scale',
            'transition-hide': 'scale',
            anchor: 'top middle',
            self: 'bottom middle',
        }, { default: () => tooltip }),
    });
}

const ReleaseActionsCellRenderer = defineComponent({
    name: 'sandbox-release-actions-cell',
    props: {
        params: { type: Object, default: null },
    },
    render() {
        const ctx = this.params?.context;
        const row = this.params?.data;
        if (!ctx || !row)
            return null;
        const isLatest = !!ctx.isLatest?.(row);
        const busy = !!ctx.busy;
        return h('div', {
            class: 'row no-wrap items-center',
            style: { gap: '2px' },
        }, [
            iconActionBtn({
                icon: 'mdi-inbox-arrow-down',
                tooltip: 'Pull release to draft',
                disable: busy,
                onClick: () => ctx.onPull?.(row),
            }),
            iconActionBtn({
                icon: isLatest ? 'mdi-star' : 'mdi-star-outline',
                tooltip: isLatest ? 'Already the latest release' : 'Set as latest release',
                color: isLatest ? 'amber-9' : 'primary',
                disable: busy || isLatest,
                onClick: () => ctx.onSetLatest?.(row),
            }),
            iconActionBtn({
                icon: 'mdi-delete-outline',
                tooltip: 'Delete release',
                color: 'negative',
                disable: busy,
                onClick: () => ctx.onDelete?.(row),
            }),
        ]);
    },
});

const ShelvesetActionsCellRenderer = defineComponent({
    name: 'sandbox-shelveset-actions-cell',
    props: {
        params: { type: Object, default: null },
    },
    render() {
        const ctx = this.params?.context;
        const row = this.params?.data;
        if (!ctx || !row)
            return null;
        return h('div', {
            class: 'row no-wrap items-center',
            style: { gap: '2px' },
        }, [
            iconActionBtn({
                icon: 'mdi-restore',
                tooltip: 'Restore shelveset to draft',
                disable: !!ctx.busy,
                onClick: () => ctx.onRestore?.(row),
            }),
            iconActionBtn({
                icon: 'mdi-delete-outline',
                tooltip: 'Delete shelveset',
                color: 'negative',
                disable: !!ctx.busy,
                onClick: () => ctx.onDelete?.(row),
            }),
        ]);
    },
});

const ACTIONS_COL = {
    headerName: '',
    colId: 'actions',
    width: 120,
    minWidth: 120,
    maxWidth: 140,
    sortable: false,
    filter: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    cellClass: 'c-flowv2-grid-actions',
    cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        textOverflow: 'clip',
        paddingLeft: '4px',
        paddingRight: '4px',
    },
};

export default defineComponent({
    name: 'flow-v2-project-sandbox-panel',
    components: {
        QBtn, QBanner, QSpinner, QDialog, QCard, QCardSection, QCardActions,
        QInput, QOptionGroup, QIcon, QTooltip, QSelect,
        AgGridVue,
        ReleaseActionsCellRenderer,
        ShelvesetActionsCellRenderer,
    },
    props: {
        projectId: { type: String, required: true },
        projectName: { type: String, default: '' },
    },
    data() {
        return {
            loading: true,
            busy: false,
            error: null,
            info: null,
            project: null,
            releases: [],
            shelvesets: [],
            basedOnReleaseId: null,

            releaseGridApi: null,
            shelvesetGridApi: null,
            releaseQuickFilter: '',
            shelvesetQuickFilter: '',

            createOpen: false,
            createLabel: '',
            createDepMode: 'refreshDeps',
            createDepsPreview: [],

            pullOpen: false,
            pullReleaseId: null,
            pullMode: 'rootOnly',
            pullShelvesetName: '',
            pullDepsPreview: [],

            deleteOpen: false,
            deleteLoading: false,
            deletePreview: null,
            deleteReplacementPin: 'draft',

            releaseColumnDefs: [
                {
                    ...ACTIONS_COL,
                    width: 120,
                    cellRenderer: 'ReleaseActionsCellRenderer',
                    pinned: 'left',
                },
                {
                    headerName: 'Version',
                    field: 'VersionLabel',
                    flex: 1.2,
                    minWidth: 120,
                    valueFormatter: (p) => p.value || releaseIdOf(p.data) || '—',
                },
                {
                    headerName: 'Created',
                    field: 'CreatedAt',
                    flex: 1.4,
                    minWidth: 160,
                    valueFormatter: (p) => formatCreatedAt(p.value),
                },
                {
                    headerName: 'Latest',
                    colId: 'latest',
                    minWidth: 100,
                    width: 110,
                    valueGetter: (p) => (p.context?.isLatest?.(p.data) ? '★' : ''),
                    sortable: false,
                    filter: false,
                    suppressHeaderMenuButton: true,
                },
                {
                    headerName: 'Deps',
                    colId: 'deps',
                    minWidth: 90,
                    width: 100,
                    valueGetter: (p) => formatDepsCount(p.data),
                    sortable: false,
                    filter: false,
                    suppressHeaderMenuButton: true,
                },
            ],
            shelvesetColumnDefs: [
                {
                    ...ACTIONS_COL,
                    width: 88,
                    minWidth: 88,
                    maxWidth: 100,
                    cellRenderer: 'ShelvesetActionsCellRenderer',
                    pinned: 'left',
                },
                {
                    headerName: 'Name',
                    field: 'Name',
                    flex: 1.5,
                    minWidth: 160,
                },
                {
                    headerName: 'Created',
                    field: 'CreatedAt',
                    flex: 1.4,
                    minWidth: 160,
                    valueFormatter: (p) => formatCreatedAt(p.value),
                },
                {
                    headerName: 'Projects',
                    colId: 'projects',
                    minWidth: 110,
                    width: 120,
                    valueGetter: (p) => String((p.data?.ProjectIDs || []).length),
                    filter: false,
                    suppressHeaderMenuButton: true,
                },
            ],
            defaultColDef: {
                sortable: true,
                resizable: true,
                filter: true,
            },
        };
    },
    computed: {
        latestReleaseId() {
            return this.project?.LatestReleaseID || null;
        },
        latestReleaseLabel() {
            return this.labelForReleaseId(this.latestReleaseId);
        },
        basedOnReleaseLabel() {
            return this.labelForReleaseId(this.basedOnReleaseId);
        },
        releaseGridContext() {
            return {
                busy: this.busy,
                isLatest: (row) => this.isLatest(row),
                onPull: (row) => this.openPull(row),
                onSetLatest: (row) => this.setLatest(row),
                onDelete: (row) => this.openDelete(row),
            };
        },
        shelvesetGridContext() {
            return {
                busy: this.busy,
                onRestore: (row) => this.restoreShelveset(row),
                onDelete: (row) => this.deleteShelveset(row),
            };
        },
        createDepModeOptions() {
            return [
                {
                    label: this.basedOnReleaseId
                        ? `Reuse frozen deps from ${this.basedOnReleaseLabel} (hotfix)`
                        : 'Reuse frozen deps from based-on release',
                    value: 'reuseBundled',
                    disable: !this.basedOnReleaseId,
                },
                {
                    label: 'Use current dependency drafts (normal)',
                    value: 'refreshDeps',
                },
            ];
        },
        createEnvelopeRows() {
            const rows = (this.createDepsPreview || []).filter((d) => d && d.Role !== 'error');
            if (rows.length)
                return rows;
            return [{
                ProjectID: this.projectId,
                Name: this.projectName || 'root',
                Role: 'root',
                Source: this.createDepMode === 'reuseBundled' ? 'basedOn' : 'draft',
            }];
        },
        createEnvelopeErrors() {
            return (this.createDepsPreview || []).filter((d) => d && d.Role === 'error');
        },
        deleteReplacementOptions() {
            const opts = [{ label: 'Draft', value: 'draft' }];
            const preview = this.deletePreview;
            if (preview && !preview.IsLatest)
                opts.push({ label: 'Latest (project pointer)', value: 'latest' });
            for (const r of preview?.AlternateReleases || []) {
                opts.push({
                    label: r.VersionLabel || r.ReleaseID,
                    value: r.ReleaseID,
                });
            }
            return opts;
        },
        deleteCanConfirm() {
            if (!this.deletePreview)
                return false;
            if (!this.deletePreview.NeedsReplacement)
                return true;
            return !!this.deleteReplacementPin;
        },
        createLabelError() {
            const label = String(this.createLabel || '').trim();
            if (!label)
                return null;
            if (isReservedReleasePin(label))
                return 'Cannot use latest or sandbox — those pins are reserved.';
            return null;
        },
        createCanConfirm() {
            const label = String(this.createLabel || '').trim();
            return !!label && !this.createLabelError;
        },
    },
    watch: {
        projectId: { immediate: true, handler() { this.refresh(); } },
        busy() {
            // AgGrid caches cell renderers; context.busy changes alone do not re-enable icons.
            this.refreshActionCells();
        },
        latestReleaseId() {
            this.refreshActionCells();
        },
        releaseQuickFilter(v) {
            this.releaseGridApi?.setGridOption?.('quickFilterText', v || '');
            if (this.releaseGridApi && typeof this.releaseGridApi.setQuickFilter === 'function')
                this.releaseGridApi.setQuickFilter(v || '');
        },
        shelvesetQuickFilter(v) {
            this.shelvesetGridApi?.setGridOption?.('quickFilterText', v || '');
            if (this.shelvesetGridApi && typeof this.shelvesetGridApi.setQuickFilter === 'function')
                this.shelvesetGridApi.setQuickFilter(v || '');
        },
    },
    methods: {
        refreshActionCells() {
            // Vue cell renderers cache disable state; context changes alone do not re-enable icons.
            // Push a fresh context onto the grid, then force-refresh the actions column.
            this.$nextTick(() => {
                const refresh = (api, context) => {
                    if (!api)
                        return;
                    api.setGridOption?.('context', context);
                    api.refreshCells?.({ force: true, columns: ['actions'] });
                    api.redrawRows?.();
                };
                refresh(this.releaseGridApi, this.releaseGridContext);
                refresh(this.shelvesetGridApi, this.shelvesetGridContext);
            });
        },
        labelForReleaseId(id) {
            if (!id)
                return '—';
            const match = (this.releases || []).find((r) => releaseIdOf(r) === id);
            return match?.VersionLabel || id;
        },
        isLatest(row) {
            const id = releaseIdOf(row);
            return !!(id && id === this.latestReleaseId);
        },
        onReleaseGridReady(params) {
            this.releaseGridApi = params.api;
        },
        onShelvesetGridReady(params) {
            this.shelvesetGridApi = params.api;
        },
        getReleaseRowId(params) {
            return String(releaseIdOf(params.data) || '');
        },
        getShelvesetRowId(params) {
            return String(params.data?.ShelvesetID || params.data?.ID || params.data?.Name || '');
        },
        async refresh() {
            if (!this.projectId)
                return;
            this.loading = true;
            this.error = null;
            try {
                const [project, releases, shelvesets] = await Promise.all([
                    utils.api.get(`Apps/FlowV2/GetProject?ID=${this.projectId}`),
                    utils.api.get(`Apps/FlowV2/ListReleases?ProjectID=${this.projectId}`).catch(() => []),
                    utils.api.get(`Apps/FlowV2/ListShelvesets?ProjectID=${this.projectId}`).catch(() => []),
                ]);
                this.project = project;
                this.releases = Array.isArray(releases) ? releases : (releases?.Result || []);
                this.shelvesets = Array.isArray(shelvesets) ? shelvesets : (shelvesets?.Result || []);
                this.basedOnReleaseId = project?.BasedOnReleaseID || null;
                if (this.basedOnReleaseId)
                    this.createDepMode = 'reuseBundled';
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.loading = false;
                this.refreshActionCells();
            }
        },
        envelopeSourceLabel(row) {
            const role = row?.Role || 'dependency';
            if (role === 'root')
                return '(root — draft)';
            if (this.createDepMode === 'reuseBundled' || row?.Source === 'basedOn')
                return `(from ${this.basedOnReleaseLabel})`;
            if (row?.Source === 'lastRelease')
                return '(from last release)';
            return '(dependency — draft)';
        },
        async resolveProjectRows(ids, roleSource) {
            const list = (ids || []).map((id) => String(id));
            const nameTasks = list.map(async (sid) => {
                let name = sid;
                try {
                    const proj = await utils.api.get(`Apps/FlowV2/GetProject?ID=${encodeURIComponent(sid)}`);
                    if (proj?.Name)
                        name = proj.Name;
                }
                catch { /* keep id */ }
                return {
                    ProjectID: sid,
                    Name: name,
                    Role: sid === this.projectId ? 'root' : 'dependency',
                    Source: roleSource,
                };
            });
            return Promise.all(nameTasks);
        },
        bestPriorReleaseBundledIds() {
            let best = [];
            for (const r of this.releases || []) {
                const ids = Array.isArray(r?.BundledProjectIDs) ? r.BundledProjectIDs : [];
                if (ids.length > best.length)
                    best = ids;
            }
            return best.map((id) => String(id));
        },
        async openCreateRelease() {
            this.createLabel = '';
            this.createDepMode = this.basedOnReleaseId ? 'reuseBundled' : 'refreshDeps';
            this.createDepsPreview = [];
            this.error = null;
            this.createOpen = true;
            await this.loadCreateDepsPreview();
        },
        async loadCreateDepsPreview() {
            this.createDepsPreview = [];
            try {
                if (this.createDepMode === 'reuseBundled' && this.basedOnReleaseId) {
                    const detail = await utils.api.get(
                        `Apps/FlowV2/GetRelease?ReleaseID=${encodeURIComponent(this.basedOnReleaseId)}`,
                    );
                    const ids = Array.isArray(detail?.BundledProjectIDs)
                        ? detail.BundledProjectIDs
                        : [];
                    const rows = await this.resolveProjectRows(ids, 'basedOn');
                    if (!rows.length) {
                        rows.push({
                            ProjectID: this.projectId,
                            Name: this.projectName || 'root',
                            Role: 'root',
                            Source: 'basedOn',
                        });
                    }
                    this.createDepsPreview = rows;
                    return;
                }

                const deps = await utils.api.get(
                    `Apps/FlowV2/ListReleaseDependencies?ProjectID=${this.projectId}`,
                );
                let list = Array.isArray(deps) ? deps : (deps?.Result || []);
                if (!Array.isArray(list))
                    list = [];

                const hasDraftDeps = list.some((d) => d?.Role === 'dependency' && d?.Source !== 'lastRelease');
                const hasAnyDeps = list.some((d) => d?.Role === 'dependency');
                if (!hasAnyDeps) {
                    const priorIds = this.bestPriorReleaseBundledIds();
                    if (priorIds.length > 1) {
                        const priorRows = await this.resolveProjectRows(priorIds, 'lastRelease');
                        const root = {
                            ProjectID: this.projectId,
                            Name: this.projectName || 'root',
                            Role: 'root',
                            Source: 'draft',
                        };
                        list = [root, ...priorRows.filter((r) => r.Role === 'dependency')];
                    }
                }
                else if (!hasDraftDeps) {
                    // API already fell back to lastRelease — keep as-is
                }

                this.createDepsPreview = list.length
                    ? list
                    : [{ ProjectID: this.projectId, Name: this.projectName || 'root', Role: 'root', Source: 'draft' }];
            }
            catch (err) {
                const priorIds = this.bestPriorReleaseBundledIds();
                if (priorIds.length > 1) {
                    try {
                        this.createDepsPreview = await this.resolveProjectRows(priorIds, 'lastRelease');
                        return;
                    }
                    catch { /* fall through */ }
                }
                this.createDepsPreview = [{
                    ProjectID: this.projectId,
                    Name: this.projectName || 'root',
                    Role: 'root',
                    Source: 'draft',
                }, {
                    ProjectID: this.projectId,
                    Name: formatApiError(err) || 'Failed to load dependency preview',
                    Role: 'error',
                    Source: 'draft',
                }];
            }
        },
        async onCreateDepModeChange(v) {
            this.createDepMode = v;
            await this.loadCreateDepsPreview();
        },
        async submitCreateRelease() {
            if (!this.createCanConfirm)
                return;
            this.busy = true;
            this.error = null;
            this.info = null;
            try {
                const body = {
                    ProjectID: this.projectId,
                    VersionLabel: String(this.createLabel).trim(),
                    BasedOnReleaseID: this.basedOnReleaseId || undefined,
                    DepMode: this.createDepMode,
                };
                const res = await utils.api.request('POST', 'Apps/FlowV2/CreateRelease', body);
                if (res && res.Success === false)
                    throw new Error(formatReleaseFailure(res, 'CreateRelease failed'));
                this.createOpen = false;
                this.info = `Created release ${res?.VersionLabel || res?.ReleaseID || ''}`.trim();
                // Clear busy before reload so action cells do not mount disabled.
                this.busy = false;
                await this.refresh();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.busy = false;
            }
        },
        async openPull(release) {
            this.pullReleaseId = releaseIdOf(release);
            this.pullMode = 'rootOnly';
            this.pullShelvesetName = defaultShelvesetName(release.VersionLabel || this.pullReleaseId);
            this.pullDepsPreview = release.BundledProjectIDs || [];
            if (!this.pullDepsPreview.length) {
                try {
                    const detail = await utils.api.get(`Apps/FlowV2/GetRelease?ReleaseID=${this.pullReleaseId}`);
                    this.pullDepsPreview = detail?.BundledProjectIDs || [];
                }
                catch { /* ignore */ }
            }
            this.pullOpen = true;
        },
        async submitPull() {
            this.busy = true;
            this.error = null;
            this.info = null;
            try {
                const body = {
                    ReleaseID: this.pullReleaseId,
                    Mode: this.pullMode,
                    ShelvesetName: this.pullMode === 'withDependencies' ? this.pullShelvesetName : undefined,
                };
                const res = await utils.api.request('POST', 'Apps/FlowV2/PullReleaseToDraft', body);
                if (res && res.Success === false)
                    throw new Error(formatReleaseFailure(res, 'Pull failed'));
                this.pullOpen = false;
                this.info = this.pullMode === 'withDependencies'
                    ? `Pulled with dependencies. Shelveset: ${res?.ShelvesetName || this.pullShelvesetName}`
                    : 'Pulled root draft from release.';
                this.busy = false;
                await this.refresh();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.busy = false;
            }
        },
        async setLatest(release) {
            this.busy = true;
            this.error = null;
            try {
                const id = releaseIdOf(release);
                const published = await utils.api.request(
                    'POST',
                    'Apps/FlowV2/PublishLatestWebUrls',
                    { ProjectID: this.projectId, ReleaseID: id },
                );
                if (published && published.Success === false)
                    throw new Error(published.Reason || 'PublishLatestWebUrls failed');
                const res = await utils.api.request(
                    'POST',
                    `Apps/FlowV2/SetLatestRelease?ProjectID=${this.projectId}&ReleaseID=${id}`,
                    {},
                );
                if (res && res.Success === false)
                    throw new Error(res.Reason || 'SetLatest failed');
                this.busy = false;
                await this.refresh();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.busy = false;
            }
        },
        async openDelete(release) {
            const id = releaseIdOf(release);
            this.deleteOpen = true;
            this.deleteLoading = true;
            this.deletePreview = null;
            this.deleteReplacementPin = 'draft';
            this.error = null;
            try {
                const preview = await utils.api.get(`Apps/FlowV2/PreviewDeleteRelease?ReleaseID=${encodeURIComponent(id)}`);
                this.deletePreview = preview;
                if (preview?.NeedsReplacement) {
                    const alts = preview.AlternateReleases || [];
                    if (!preview.IsLatest)
                        this.deleteReplacementPin = 'latest';
                    else if (alts.length)
                        this.deleteReplacementPin = alts[0].ReleaseID;
                    else
                        this.deleteReplacementPin = 'draft';
                }
            }
            catch (err) {
                this.error = formatApiError(err);
                this.deleteOpen = false;
            }
            finally {
                this.deleteLoading = false;
            }
        },
        async submitDelete() {
            if (!this.deletePreview || !this.deleteCanConfirm)
                return;
            this.busy = true;
            this.error = null;
            this.info = null;
            try {
                const body = {
                    ReleaseID: this.deletePreview.ReleaseID,
                };
                if (this.deletePreview.NeedsReplacement)
                    body.ReplacementPin = this.deleteReplacementPin;
                const res = await utils.api.request('POST', 'Apps/FlowV2/DeleteRelease', body);
                if (res && res.Success === false)
                    throw new Error(formatReleaseFailure(res, 'DeleteRelease failed'));
                this.deleteOpen = false;
                const label = this.deletePreview.VersionLabel || this.deletePreview.ReleaseID;
                const tp = res?.TouchpointsUpdated || 0;
                this.info = tp > 0
                    ? `Deleted release ${label} (retargeted ${tp} touchpoint${tp === 1 ? '' : 's'})`
                    : `Deleted release ${label}`;
                this.deletePreview = null;
                this.busy = false;
                await this.refresh();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.busy = false;
            }
        },
        async restoreShelveset(row) {
            if (!confirm(`Restore shelveset "${row.Name}"? This overwrites current live drafts for its projects.`))
                return;
            this.busy = true;
            this.error = null;
            try {
                const res = await utils.api.request(
                    'POST',
                    `Apps/FlowV2/RestoreShelveset?ShelvesetID=${row.ShelvesetID || row.ID}`,
                    {},
                );
                if (res && res.Success === false)
                    throw new Error(res.Reason || 'Restore failed');
                this.info = `Restored shelveset ${row.Name}`;
                this.busy = false;
                await this.refresh();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.busy = false;
            }
        },
        async deleteShelveset(row) {
            const id = row.ShelvesetID || row.ID;
            if (!id)
                return;
            if (!confirm(`Delete shelveset "${row.Name || id}"? This cannot be undone.`))
                return;
            this.busy = true;
            this.error = null;
            this.info = null;
            try {
                const res = await utils.api.request(
                    'POST',
                    `Apps/FlowV2/DeleteShelveset?ShelvesetID=${encodeURIComponent(id)}`,
                    {},
                );
                if (res && res.Success === false)
                    throw new Error(res.Reason || 'Delete shelveset failed');
                this.info = `Deleted shelveset ${row.Name || id}`;
                this.busy = false;
                await this.refresh();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.busy = false;
            }
        },
    },
    render() {
        if (this.loading) {
            return (
                <div class="q-pa-lg flex flex-center">
                    <QSpinner color="primary" size="40px" />
                </div>
            );
        }

        return (
            <div class="q-pa-md" style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>
                <QBanner class="bg-amber-1" rounded>
                    {{
                        avatar: () => <QIcon name="mdi-alert" color="amber-9" />,
                        default: () => `Creating a release freezes all methods in ${this.projectName || 'this project'} plus bundled FlowV2 dependencies.`,
                    }}
                </QBanner>

                {this.error ? <QBanner class="bg-red-1 text-negative" rounded>{this.error}</QBanner> : null}
                {this.info ? <QBanner class="bg-green-1 text-positive" rounded>{this.info}</QBanner> : null}

                <div class="row items-center q-gutter-sm">
                    <div class="col">
                        <div class="text-body2">
                            Release management · <strong>{this.projectName || this.projectId}</strong>
                        </div>
                        <div class="text-caption text-grey-7">
                            Latest: {this.latestReleaseLabel}
                            {this.basedOnReleaseId
                                ? <span> · Based on: {this.basedOnReleaseLabel}</span>
                                : null}
                        </div>
                    </div>
                    <QBtn
                        color="primary"
                        unelevated
                        no-caps
                        class={BTN}
                        label="Create Release"
                        disable={this.busy}
                        onClick={() => this.openCreateRelease()}
                    />
                </div>

                <div>
                    <div class="text-subtitle2 q-mb-xs">Releases</div>
                    <QInput
                        dense
                        hide-hint
                        hide-bottom-space
                        clearable
                        placeholder="Quick Search"
                        modelValue={this.releaseQuickFilter}
                        onUpdate:modelValue={(v) => { this.releaseQuickFilter = v || ''; }}
                        class="q-mb-none quick-search-input"
                        style={{ maxWidth: '280px', width: '280px', alignSelf: 'flex-start' }}
                        v-slots={{ prepend: () => <QIcon name="mdi-magnify" /> }}
                    />
                    <div
                        class="ag-theme-alpine"
                        style={{
                            height: '280px',
                            width: '100%',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        <AgGridVue
                            style={{ width: '100%', height: '100%' }}
                            class="ag-theme-alpine"
                            columnDefs={this.releaseColumnDefs}
                            defaultColDef={this.defaultColDef}
                            rowData={this.releases}
                            getRowId={this.getReleaseRowId}
                            context={this.releaseGridContext}
                            components={{ ReleaseActionsCellRenderer }}
                            headerHeight={32}
                            rowHeight={36}
                            pagination={true}
                            paginationPageSize={25}
                            suppressCellFocus={true}
                            animateRows={false}
                            onGridReady={this.onReleaseGridReady}
                            overlayNoRowsTemplate="'No releases yet'"
                        />
                    </div>
                </div>

                <div>
                    <div class="text-subtitle2 q-mb-xs">Shelvesets</div>
                    <QInput
                        dense
                        hide-hint
                        hide-bottom-space
                        clearable
                        placeholder="Quick Search"
                        modelValue={this.shelvesetQuickFilter}
                        onUpdate:modelValue={(v) => { this.shelvesetQuickFilter = v || ''; }}
                        class="q-mb-none quick-search-input"
                        style={{ maxWidth: '280px', width: '280px', alignSelf: 'flex-start' }}
                        v-slots={{ prepend: () => <QIcon name="mdi-magnify" /> }}
                    />
                    <div
                        class="ag-theme-alpine"
                        style={{
                            height: '220px',
                            width: '100%',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        <AgGridVue
                            style={{ width: '100%', height: '100%' }}
                            class="ag-theme-alpine"
                            columnDefs={this.shelvesetColumnDefs}
                            defaultColDef={this.defaultColDef}
                            rowData={this.shelvesets}
                            getRowId={this.getShelvesetRowId}
                            context={this.shelvesetGridContext}
                            components={{ ShelvesetActionsCellRenderer }}
                            headerHeight={32}
                            rowHeight={36}
                            pagination={true}
                            paginationPageSize={25}
                            suppressCellFocus={true}
                            animateRows={false}
                            onGridReady={this.onShelvesetGridReady}
                            overlayNoRowsTemplate="'No shelvesets for this project'"
                        />
                    </div>
                </div>

                <QDialog modelValue={this.createOpen} onUpdate:modelValue={(v) => { this.createOpen = v; }} persistent>
                    <QCard style={{ minWidth: '420px' }}>
                        <QCardSection>
                            <div class="text-h6">Create Release — {this.projectName || this.projectId}</div>
                        </QCardSection>
                        {this.error ? (
                            <QCardSection class="q-pt-none">
                                <QBanner class="bg-red-1 text-negative" dense rounded>{this.error}</QBanner>
                            </QCardSection>
                        ) : null}
                        <QCardSection>
                            <QInput
                                dense
                                outlined
                                required
                                label="Version label"
                                modelValue={this.createLabel}
                                error={!!this.createLabelError}
                                error-message={this.createLabelError || ''}
                                hint="Letters, digits, dot, hyphen, and underscore only. Used as the URL pin."
                                onUpdate:modelValue={(v) => { this.createLabel = filterReleasePinInput(v); }}
                                class="q-mb-md"
                            />
                            {this.basedOnReleaseId ? (
                                <div class="q-mb-md text-caption">Based on: {this.basedOnReleaseLabel}</div>
                            ) : null}
                            <div class="text-subtitle2 q-mb-sm">Dependencies</div>
                            <div class="text-caption text-grey-7 q-mb-sm">
                                Hotfix reuses the based-on release&apos;s frozen dependency builds and only rebuilds this project from draft.
                                Normal re-bundles whatever dependency drafts are live now.
                            </div>
                            <QOptionGroup
                                modelValue={this.createDepMode}
                                onUpdate:modelValue={(v) => { this.onCreateDepModeChange(v); }}
                                options={this.createDepModeOptions}
                            />
                            <div class="q-mt-md text-caption">
                                Envelope will include:
                                <ul>
                                    {this.createEnvelopeRows.map((d, i) =>
                                        <li key={`${d.ProjectID || d.Name}-${i}`}>
                                            {d.Name || d.ProjectID} {this.envelopeSourceLabel(d)}
                                        </li>
                                    )}
                                </ul>
                                {this.createEnvelopeErrors.length ? (
                                    <div class="text-negative q-mt-sm">
                                        Discover notes:
                                        <ul>
                                            {this.createEnvelopeErrors.map((d, i) =>
                                                <li key={`err-${i}`}>{d.Name || d.ProjectID}</li>
                                            )}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                        </QCardSection>
                        <QCardActions align="right">
                            <QBtn flat no-caps class={BTN} label="Cancel" onClick={() => { this.createOpen = false; }} />
                            <QBtn color="primary" unelevated no-caps class={BTN} label="Create Release" loading={this.busy} disable={this.busy || !this.createCanConfirm} onClick={() => this.submitCreateRelease()} />
                        </QCardActions>
                    </QCard>
                </QDialog>

                <QDialog modelValue={this.pullOpen} onUpdate:modelValue={(v) => { this.pullOpen = v; }} persistent>
                    <QCard style={{ minWidth: '440px' }}>
                        <QCardSection>
                            <div class="text-h6">Pull release → draft</div>
                            <div class="text-caption text-grey-7">
                                {this.labelForReleaseId(this.pullReleaseId)}
                            </div>
                        </QCardSection>
                        <QCardSection>
                            <QOptionGroup
                                modelValue={this.pullMode}
                                onUpdate:modelValue={(v) => { this.pullMode = v; }}
                                options={[
                                    { label: 'Root project only', value: 'rootOnly' },
                                    { label: 'Include all dependencies', value: 'withDependencies' },
                                ]}
                            />
                            {this.pullMode === 'withDependencies' ? (
                                <div class="q-mt-md">
                                    <QInput
                                        dense
                                        outlined
                                        label="Shelve overwritten drafts as"
                                        modelValue={this.pullShelvesetName}
                                        onUpdate:modelValue={(v) => { this.pullShelvesetName = v; }}
                                    />
                                    <QBanner class="bg-amber-1 q-mt-md" dense rounded>
                                        Shared dependencies also overwrite other projects&apos; working drafts until you restore the shelveset.
                                    </QBanner>
                                </div>
                            ) : null}
                            <div class="q-mt-md text-caption">
                                Will restore:
                                <ul>
                                    <li>{this.projectName || this.projectId}</li>
                                    {this.pullMode === 'withDependencies'
                                        ? (this.pullDepsPreview || []).filter((id) => id !== this.projectId).map((id) =>
                                            <li key={id}>{id}</li>
                                        )
                                        : null}
                                </ul>
                            </div>
                        </QCardSection>
                        <QCardActions align="right">
                            <QBtn flat no-caps class={BTN} label="Cancel" onClick={() => { this.pullOpen = false; }} />
                            <QBtn color="primary" unelevated no-caps class={BTN} label="Pull to draft" loading={this.busy} onClick={() => this.submitPull()} />
                        </QCardActions>
                    </QCard>
                </QDialog>

                <QDialog
                    modelValue={this.deleteOpen}
                    onUpdate:modelValue={(v) => { this.deleteOpen = v; }}
                    persistent={this.busy || this.deleteLoading}
                >
                    <QCard style={{ minWidth: '440px', maxWidth: '560px' }}>
                        <QCardSection>
                            <div class="text-h6">Delete release</div>
                            <div class="text-caption text-grey-7">
                                {this.deletePreview?.VersionLabel || this.deletePreview?.ReleaseID || '…'}
                            </div>
                        </QCardSection>
                        <QCardSection>
                            {this.deleteLoading ? (
                                <div class="flex flex-center q-pa-md"><QSpinner color="primary" /></div>
                            ) : this.deletePreview ? (
                                <div>
                                    <QBanner class="bg-amber-1 q-mb-md" dense rounded>
                                        This permanently removes the release envelope and generated artifacts.
                                    </QBanner>
                                    <ul class="q-mb-md text-body2" style={{ paddingLeft: '1.25rem' }}>
                                        {this.deletePreview.IsLatest
                                            ? <li>This is the project&apos;s <strong>Latest</strong> release.</li>
                                            : null}
                                        {this.deletePreview.ProjectBasedOnThis
                                            ? <li>Project <strong>Based on</strong> points at this release.</li>
                                            : null}
                                        {(this.deletePreview.Touchpoints || []).length > 0
                                            ? <li>{(this.deletePreview.Touchpoints || []).length} touchpoint(s) pin to this release.</li>
                                            : null}
                                        {(this.deletePreview.DependentReleases || []).length > 0
                                            ? <li>{(this.deletePreview.DependentReleases || []).length} other release(s) list this as BasedOn.</li>
                                            : null}
                                        {!this.deletePreview.NeedsReplacement
                                            ? <li>No dependents — safe to delete.</li>
                                            : null}
                                    </ul>
                                    {this.deletePreview.NeedsReplacement ? (
                                        <QSelect
                                            dense
                                            outlined
                                            emit-value
                                            map-options
                                            label="Retarget dependents to"
                                            options={this.deleteReplacementOptions}
                                            modelValue={this.deleteReplacementPin}
                                            onUpdate:modelValue={(v) => { this.deleteReplacementPin = v; }}
                                            hint="Applies to Latest, BasedOn, touchpoint pins, and dependent releases"
                                        />
                                    ) : null}
                                </div>
                            ) : (
                                <div class="text-negative">Could not load delete preview.</div>
                            )}
                        </QCardSection>
                        <QCardActions align="right">
                            <QBtn
                                flat
                                no-caps
                                class={BTN}
                                label="Cancel"
                                disable={this.busy}
                                onClick={() => { this.deleteOpen = false; }}
                            />
                            <QBtn
                                color="negative"
                                unelevated
                                no-caps
                                class={BTN}
                                label="Delete release"
                                loading={this.busy}
                                disable={this.deleteLoading || !this.deleteCanConfirm}
                                onClick={() => this.submitDelete()}
                            />
                        </QCardActions>
                    </QCard>
                </QDialog>
            </div>
        );
    },
});
